import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import type { ISupportCategory } from '../../../models/Support/ISupportCategory';
import type { ISupportAgentConfig } from '../../../queries/Support/useSupportTeam';

/**
 * Ad-hoc criteria for the "Search criteria" popover. Criteria narrow independent dimensions of a
 * left-rail preset and replace that preset's constraint when they target the same dimension. State
 * stays local to the inbox alongside the free-text search box.
 */
export interface InboxFilters {
	status?: TicketStatus;
	priority?: TicketPriority;
	categoryId?: string;
	assigneeId?: string;
	/**
	 * Contact refinement from the table's Contact header: a case-insensitive "contains" match on the
	 * ticket's contact name or email. Only the ticket-list (filter) endpoint applies it — free-text
	 * search already matches the contact fields on its own, so the header hides this filter there.
	 */
	contact?: string;
	/**
	 * CSM refinement, picked from the same employee autocomplete as account details' "assign CSM"
	 * field. Both id and name are kept: the id rehydrates the combobox, while the name is what the
	 * backend matches (the search endpoint resolves it against each account's customer-success name —
	 * a ticket's CSM lives on its account, not on the ticket itself).
	 */
	csmId?: string;
	csmName?: string;
	sort: InboxSort;
}

export type InboxSort = 'newest' | 'oldest' | 'priority';

export type InboxView = 'desk' | 'table';

/** Stable control key for the "no filter" option (base-ui needs a non-empty string value). */
export const FILTER_ANY = 'any';

export const DEFAULT_INBOX_FILTERS: InboxFilters = { sort: 'newest' };

/**
 * One selectable value in a filter control. `key` belongs to the DOM control while `value` stays in
 * the domain type, so callers never have to cast an arbitrary string into a ticket enum.
 */
export interface InboxFilterOption<T> {
	key: string;
	value: T;
	label: string;
}

/** Status choices shared by the Search criteria popover and the table view's Status header menu. */
export const STATUS_FILTER_OPTIONS: ReadonlyArray<InboxFilterOption<TicketStatus | undefined>> = [
	{ key: FILTER_ANY, value: undefined, label: 'Any' },
	{ key: TicketStatus.New, value: TicketStatus.New, label: 'New' },
	{ key: TicketStatus.Open, value: TicketStatus.Open, label: 'Open' },
	{ key: TicketStatus.Pending, value: TicketStatus.Pending, label: 'Pending' },
	{ key: TicketStatus.Closed, value: TicketStatus.Closed, label: 'Closed' },
	{ key: TicketStatus.Spam, value: TicketStatus.Spam, label: 'Spam' },
];

/** Priority choices shared by the Search criteria popover and the table view's Priority header menu. */
export const PRIORITY_FILTER_OPTIONS: ReadonlyArray<InboxFilterOption<TicketPriority | undefined>> = [
	{ key: FILTER_ANY, value: undefined, label: 'Any' },
	{ key: TicketPriority.Urgent, value: TicketPriority.Urgent, label: 'Urgent' },
	{ key: TicketPriority.High, value: TicketPriority.High, label: 'High' },
	{ key: TicketPriority.Normal, value: TicketPriority.Normal, label: 'Normal' },
	{ key: TicketPriority.Low, value: TicketPriority.Low, label: 'Low' },
];

export function getFilterOptionKey<T>(options: ReadonlyArray<InboxFilterOption<T>>, value: T): string {
	return options.find(option => Object.is(option.value, value))?.key ?? options[0]?.key ?? '';
}

export function getFilterOptionLabel<T>(options: ReadonlyArray<InboxFilterOption<T>>, value: T): string {
	return options.find(option => Object.is(option.value, value))?.label ?? String(value ?? 'Unknown');
}

export interface PreparedInboxFilterChoices {
	activeCategories: ISupportCategory[];
	categoryOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	assigneeOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
}

/** Prepare the shared filter choices once per host so every search/filter surface uses the same set. */
export function prepareInboxFilterChoices(
	categories: readonly ISupportCategory[],
	teamMembers: ReadonlyArray<Pick<ISupportAgentConfig, 'agentId' | 'agentName'>>
): PreparedInboxFilterChoices {
	const activeCategories = categories.filter(category => category.isActive);
	return {
		activeCategories,
		categoryOptions: [
			{ key: FILTER_ANY, value: undefined, label: 'Any' },
			...activeCategories.map(category => ({
				key: `category:${category.id}`,
				value: category.id,
				label: category.name,
			})),
		],
		assigneeOptions: [
			{ key: FILTER_ANY, value: undefined, label: 'Anyone' },
			...teamMembers.map(member => ({
				key: `assignee:${member.agentId}`,
				value: member.agentId,
				label: member.agentName || 'Unnamed agent',
			})),
		],
	};
}

/**
 * Count of active narrowing filters, shown as the badge on the filter button. Sort is excluded on
 * purpose — it reorders rather than narrows, so it should not read as a "result-limiting" filter.
 */
export function countActiveInboxFilters(filters: InboxFilters): number {
	let count = 0;
	if (filters.status) count += 1;
	if (filters.priority) count += 1;
	if (filters.categoryId) count += 1;
	if (filters.assigneeId) count += 1;
	if (filters.contact) count += 1;
	if (filters.csmName) count += 1;
	return count;
}

/**
 * Map the popover's sort choice onto the ticket-list endpoint's `sort`/`sortBy` params. Newest is the
 * backend default (no params); oldest flips the default date ordering; priority sorts by priority
 * descending, matching the existing "Priority" view.
 */
export function sortToListParams(sort: InboxSort): { sort?: 'asc' | 'desc'; sortBy?: string } {
	if (sort === 'priority') return { sort: 'desc', sortBy: 'Priority' };
	if (sort === 'oldest') return { sort: 'asc' };
	return {};
}
